import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Users, Package, Repeat, ShieldCheck, Crown, Wallet, Ban, RotateCcw, Mail, AlertTriangle, Coins, ExternalLink, Gift, XCircle, TrendingUp, ArrowUpFromLine } from "lucide-react";

function Stat({ icon: Icon, label, value, gold }) {
  return (
    <div className={`rounded-2xl p-5 border-2 border-black nb-shadow ${gold ? "bg-[#E6B31E] text-black" : "bg-white"}`}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase opacity-70"><Icon size={15} strokeWidth={3} /> {label}</div>
      <div className="font-display font-black text-3xl mt-1">{value}</div>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [trades, setTrades] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [messages, setMessages] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [activity, setActivity] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);

  const load = useCallback(async () => {
    try {
      const [s, t, u, msg, d, w, a, wd] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/trades"), api.get("/admin/users"), api.get("/admin/support"), api.get("/admin/disputes"), api.get("/admin/wallet"), api.get("/admin/membership-activity"), api.get("/admin/withdrawals"),
      ]);
      setStats(s.data); setTrades(t.data); setUsers(u.data); setMessages(msg.data); setDisputes(d.data); setWallet(w.data); setActivity(a.data); setWithdrawals(wd.data);
    } catch (e) { toast.error("Failed to load admin data"); }
  }, []);

  const confirmWithdrawal = async (w) => {
    const txid = window.prompt(`Enter the on-chain payout TXID for sending ${w.amount} USDT to ${w.address}:`);
    if (!txid) return;
    try {
      await api.post(`/admin/withdrawals/${w.id}/confirm`, { txid });
      toast.success("Withdrawal confirmed on-chain");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
  };

  const cancelWithdrawal = async (w) => {
    if (!window.confirm(`Cancel & refund ${w.amount} USDT to ${w.username}?`)) return;
    try {
      await api.post(`/admin/withdrawals/${w.id}/cancel`);
      toast.success("Withdrawal cancelled and refunded");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const markRead = async (id) => {
    try { await api.post(`/admin/support/${id}/read`); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const resolveDispute = async (id) => {
    try { await api.post(`/admin/disputes/${id}/resolve`); toast.success("Dispute resolved"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const toggleBan = async (u) => {
    const action = u.is_banned ? "unban" : "ban";
    try {
      await api.post(`/admin/users/${u.user_id}/${action}`);
      toast.success(u.is_banned ? "User unbanned" : "User banned");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  useEffect(() => { load(); }, [load]);

  if (!stats) return <div className="py-20 text-center font-display font-extrabold text-xl animate-pulse">Loading house dashboard…</div>;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="text-[#B8860B]" size={28} strokeWidth={2.5} />
        <h1 className="font-display font-black text-4xl tracking-tighter">House Exchange</h1>
      </div>
      <p className="text-black/50 font-medium mb-8">Overview of the marketplace — users, listings, and trades. No platform fees are charged.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={Crown} label="USDT Revenue" value={stats.membership_revenue_usdt} gold />
        <Stat icon={Crown} label="Members" value={stats.members} />
        <Stat icon={Repeat} label="Active Trades" value={stats.active_trades} />
        <Stat icon={ShieldCheck} label="Completed" value={stats.completed_trades} />
        <Stat icon={Users} label="Users" value={stats.total_users} />
        <Stat icon={Package} label="Listings" value={stats.total_listings} />
      </div>

      <div className="bg-black text-white rounded-2xl p-5 nb-shadow mb-8 border-2 border-[#E6B31E]" data-testid="wallet-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-display font-extrabold text-lg"><Wallet size={18} className="text-[#E6B31E]" /> Membership receiving wallet</div>
            <div className="font-mono text-sm break-all text-white/60 mt-1">{stats.membership_wallet}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase text-white/40">Live on-chain balance</div>
            <div className="font-display font-black text-3xl text-[#E6B31E] flex items-center gap-2 justify-end" data-testid="wallet-balance">
              <Coins size={22} strokeWidth={2.5} />
              {wallet?.balance_usdt != null ? `${wallet.balance_usdt.toLocaleString()} USDT` : "—"}
            </div>
          </div>
        </div>
        <div className="text-xs text-white/40 font-semibold mt-2">USDT (TRC-20) payments are verified on-chain via the public Tron network and land directly in this wallet.</div>

        <div className="mt-4 border-t border-white/10 pt-3">
          <div className="text-[11px] font-bold uppercase text-white/50 mb-2">Recent incoming USDT transfers (on-chain)</div>
          {!wallet ? (
            <div className="text-white/40 text-sm font-semibold py-2">Loading…</div>
          ) : wallet.recent.length === 0 ? (
            <div className="text-white/40 text-sm font-semibold py-2">No incoming transfers found on-chain.</div>
          ) : (
            <div className="space-y-2 max-h-[280px] overflow-y-auto">
              {wallet.recent.map((tx) => (
                <div key={tx.txid} data-testid={`wallet-tx-${tx.txid}`} className="flex items-center justify-between gap-3 bg-white/5 rounded-lg px-3 py-2">
                  <div className="min-w-0">
                    <div className="font-extrabold text-sm text-[#E6B31E]">+{tx.amount.toLocaleString()} USDT</div>
                    <div className="text-[11px] text-white/40 font-mono truncate">from {tx.from}</div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-[11px] text-white/40 font-semibold">{tx.timestamp ? new Date(tx.timestamp).toLocaleDateString() : ""}</span>
                    <a href={`https://tronscan.org/#/transaction/${tx.txid}`} target="_blank" rel="noreferrer" className="text-white/50 hover:text-[#E6B31E]" title="View on TronScan">
                      <ExternalLink size={15} />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <h2 className="font-display font-extrabold text-2xl mb-4 flex items-center gap-2">
        <AlertTriangle size={22} /> Disputes
        {disputes.filter((d) => d.status === "open").length > 0 && (
          <span className="text-xs font-extrabold bg-red-500 text-white rounded-full px-2.5 py-0.5">{disputes.filter((d) => d.status === "open").length} open</span>
        )}
      </h2>
      <div className="bg-white nb-border rounded-2xl nb-shadow overflow-hidden mb-8">
        {disputes.length === 0 ? <div className="p-8 text-center text-black/40 font-semibold">No disputes raised.</div> : (
          <div className="divide-y-2 divide-black/5">
            {disputes.map((d) => (
              <div key={d.id} data-testid={`dispute-${d.id}`} className={`p-4 flex items-start gap-3 ${d.status === "open" ? "bg-red-50" : ""}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-sm truncate">{d.listing_title} <span className="text-black/40 font-semibold">· raised by {d.by_name}</span></div>
                  <p className="text-sm font-medium mt-1 whitespace-pre-wrap">{d.reason}</p>
                </div>
                {d.status === "open" ? (
                  <button data-testid={`resolve-${d.id}`} onClick={() => resolveDispute(d.id)} className="font-bold text-xs bg-black text-[#E6B31E] rounded-full px-3 py-1.5 border-2 border-black flex-shrink-0">Resolve</button>
                ) : (
                  <span className="text-xs font-extrabold text-green-700 flex items-center gap-1"><ShieldCheck size={14} strokeWidth={3} /> Resolved</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <h2 className="font-display font-extrabold text-2xl mb-4 flex items-center gap-2">
        <Mail size={22} /> Inbox
        {messages.filter((m) => !m.read).length > 0 && (
          <span className="text-xs font-extrabold bg-[#E6B31E] text-black rounded-full px-2.5 py-0.5">{messages.filter((m) => !m.read).length} new</span>
        )}
      </h2>
      <div className="bg-white nb-border rounded-2xl nb-shadow overflow-hidden mb-8">
        {messages.length === 0 ? <div className="p-8 text-center text-black/40 font-semibold">No messages from users yet.</div> : (
          <div className="divide-y-2 divide-black/5 max-h-[420px] overflow-y-auto">
            {messages.map((m) => (
              <div key={m.id} data-testid={`support-msg-${m.id}`} className={`p-4 flex items-start gap-3 ${!m.read ? "bg-[#FBF7E9]" : ""}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-sm flex items-center gap-2">
                    {m.from_username || m.from_name}
                    {!m.read && <span className="text-[9px] font-extrabold uppercase bg-[#E6B31E] text-black rounded-full px-2 py-0.5">New</span>}
                  </div>
                  <div className="text-[11px] text-black/40 font-medium">{m.from_email || ""}</div>
                  <p className="text-sm font-medium mt-1 whitespace-pre-wrap">{m.text}</p>
                </div>
                {!m.read && (
                  <button data-testid={`mark-read-${m.id}`} onClick={() => markRead(m.id)} className="font-bold text-xs bg-black text-[#E6B31E] rounded-full px-3 py-1.5 border-2 border-black flex-shrink-0">Mark read</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-extrabold text-2xl">Members & users</h2>
        <Link to="/admin/members" data-testid="view-all-members" className="flex items-center gap-1.5 font-bold text-sm bg-black text-[#E6B31E] rounded-full px-4 py-2 border-2 border-black hover:opacity-90">
          <Crown size={15} strokeWidth={3} /> View all members
        </Link>
      </div>
      <div className="bg-white nb-border rounded-2xl nb-shadow overflow-hidden mb-8">
        <div className="p-4 border-b-2 border-black/5">
          <input data-testid="user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by username, name or email…"
            className="w-full p-2.5 nb-border rounded-xl font-medium outline-none focus:ring-2 focus:ring-[#E6B31E]" />
        </div>
        {users.length === 0 ? <div className="p-8 text-center text-black/40 font-semibold">No users yet.</div> : (
          <div className="divide-y-2 divide-black/5 max-h-[520px] overflow-y-auto">
            {users.filter((u) => {
              const s = q.toLowerCase();
              return !s || (u.username || "").toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s);
            }).map((u) => (
              <div key={u.user_id} data-testid={`admin-user-${u.user_id}`} className="p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold truncate flex items-center gap-2">
                    {u.username || u.name}
                    {u.is_member && <Crown size={13} className="text-[#B8860B]" strokeWidth={3} />}
                    {u.is_banned && <span className="text-[10px] font-extrabold uppercase rounded-full px-2 py-0.5 border-2 border-black bg-red-200 text-red-800">Banned</span>}
                  </div>
                  <div className="text-xs text-black/50 font-medium truncate">{u.email || "—"}</div>
                </div>
                <button data-testid={`ban-btn-${u.user_id}`} onClick={() => toggleBan(u)}
                  className={`flex items-center gap-1 font-bold text-sm rounded-full px-4 py-2 border-2 border-black ${u.is_banned ? "bg-white text-black" : "bg-red-500 text-white"}`}>
                  {u.is_banned ? <><RotateCcw size={14} strokeWidth={3} /> Unban</> : <><Ban size={14} strokeWidth={3} /> Ban</>}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <ArrowUpFromLine size={22} className="text-[#B8860B]" strokeWidth={2.5} />
        <h2 className="font-display font-extrabold text-2xl">Withdrawal requests</h2>
        {withdrawals.filter((w) => w.status === "pending").length > 0 && (
          <span className="bg-orange-500 text-white text-xs font-extrabold rounded-full px-2.5 py-1" data-testid="pending-withdrawals-count">{withdrawals.filter((w) => w.status === "pending").length} pending</span>
        )}
      </div>
      <div className="bg-white nb-border rounded-2xl nb-shadow overflow-hidden mb-8">
        {withdrawals.length === 0 ? (
          <div className="p-8 text-center text-black/40 font-semibold">No withdrawal requests yet.</div>
        ) : (
          <div className="divide-y-2 divide-black/5 max-h-[420px] overflow-y-auto">
            {withdrawals.map((w) => (
              <div key={w.id} data-testid={`withdrawal-${w.id}`} className="p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold truncate">{w.username} · <span className="text-[#B8860B]">{w.amount} USDT</span></div>
                  <div className="text-xs text-black/40 font-mono truncate">to {w.address}</div>
                  <div className="text-[11px] text-black/40 font-medium">{new Date(w.created_at).toLocaleString()}</div>
                </div>
                {w.status === "pending" ? (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button data-testid={`confirm-withdrawal-${w.id}`} onClick={() => confirmWithdrawal(w)} className="flex items-center gap-1 font-bold text-sm rounded-full px-3 py-2 border-2 border-black bg-green-600 text-white"><ShieldCheck size={14} strokeWidth={3} /> Confirm payout</button>
                    <button data-testid={`cancel-withdrawal-${w.id}`} onClick={() => cancelWithdrawal(w)} className="flex items-center gap-1 font-bold text-sm rounded-full px-3 py-2 border-2 border-black bg-white text-black"><XCircle size={14} strokeWidth={3} /> Cancel</button>
                  </div>
                ) : w.status === "confirmed" ? (
                  <span className="flex items-center gap-2 flex-shrink-0">
                    <span className="flex items-center gap-1 text-green-700 font-extrabold text-sm"><ShieldCheck size={16} strokeWidth={3} /> Paid</span>
                    {w.txid && <a href={`https://tronscan.org/#/transaction/${w.txid}`} target="_blank" rel="noreferrer" className="text-black/40 hover:text-[#B8860B]" title="View on TronScan"><ExternalLink size={15} /></a>}
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-red-600 font-extrabold text-sm flex-shrink-0"><XCircle size={16} strokeWidth={3} /> Cancelled</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <TrendingUp size={22} className="text-[#B8860B]" strokeWidth={2.5} />
        <h2 className="font-display font-extrabold text-2xl">Membership activity</h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="bg-black text-white rounded-2xl p-5 border-2 border-[#E6B31E] nb-shadow" data-testid="stat-real-revenue">
          <div className="text-[10px] font-bold uppercase text-white/50">Real USDT revenue</div>
          <div className="font-display font-black text-3xl text-[#E6B31E] flex items-center gap-1.5 mt-1"><Coins size={20} strokeWidth={2.5} />{activity ? activity.real_revenue_usdt.toLocaleString() : "—"}</div>
          <div className="text-[11px] text-white/40 font-semibold mt-0.5">from paid renewals only</div>
        </div>
        <div className="bg-white nb-border rounded-2xl p-5 nb-shadow" data-testid="stat-paid-count">
          <div className="text-[10px] font-bold uppercase text-black/40">Paid renewals</div>
          <div className="font-display font-black text-3xl flex items-center gap-1.5 mt-1"><ShieldCheck size={20} className="text-green-600" strokeWidth={2.5} />{activity ? activity.paid_count : "—"}</div>
        </div>
        <div className="bg-white nb-border rounded-2xl p-5 nb-shadow" data-testid="stat-grant-count">
          <div className="text-[10px] font-bold uppercase text-black/40">House grants</div>
          <div className="font-display font-black text-3xl flex items-center gap-1.5 mt-1"><Gift size={20} className="text-[#B8860B]" strokeWidth={2.5} />{activity ? activity.grant_count : "—"}</div>
        </div>
        <div className="bg-white nb-border rounded-2xl p-5 nb-shadow" data-testid="stat-revoke-count">
          <div className="text-[10px] font-bold uppercase text-black/40">Revokes</div>
          <div className="font-display font-black text-3xl flex items-center gap-1.5 mt-1"><XCircle size={20} className="text-red-500" strokeWidth={2.5} />{activity ? activity.revoke_count : "—"}</div>
        </div>
      </div>

      <div className="bg-white nb-border rounded-2xl nb-shadow overflow-hidden mb-8">
        {!activity || activity.items.length === 0 ? (
          <div className="p-8 text-center text-black/40 font-semibold">No membership activity yet.</div>
        ) : (
          <div className="divide-y-2 divide-black/5 max-h-[420px] overflow-y-auto">
            {activity.items.map((it) => (
              <div key={it.id} data-testid={`activity-${it.type}-${it.id}`} className="p-4 flex items-center gap-3">
                <div className="flex-shrink-0">
                  {it.type === "paid" && <span className="w-9 h-9 rounded-lg bg-green-100 text-green-700 flex items-center justify-center"><Coins size={18} strokeWidth={2.5} /></span>}
                  {it.type === "grant" && <span className="w-9 h-9 rounded-lg bg-[#FBF0CE] text-[#B8860B] flex items-center justify-center"><Gift size={18} strokeWidth={2.5} /></span>}
                  {it.type === "revoke" && <span className="w-9 h-9 rounded-lg bg-red-100 text-red-600 flex items-center justify-center"><XCircle size={18} strokeWidth={2.5} /></span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold truncate">
                    {it.username}
                    {it.type === "paid" && <span className="text-green-700"> · paid {it.amount} {it.currency}</span>}
                    {it.type === "grant" && <span className="text-[#B8860B]"> · House granted {it.months || 1} month{(it.months || 1) > 1 ? "s" : ""}</span>}
                    {it.type === "revoke" && <span className="text-red-600"> · membership revoked</span>}
                  </div>
                  <div className="text-xs text-black/40 font-medium truncate">
                    {it.txid && !String(it.txid).startsWith("HOUSE_GRANT_") ? `TXID: ${it.txid}` : ""}
                    {it.created_at ? `${it.txid && !String(it.txid).startsWith("HOUSE_GRANT_") ? " · " : ""}${new Date(it.created_at).toLocaleString()}` : ""}
                  </div>
                </div>
                {it.type === "paid" && !String(it.txid).startsWith("HOUSE_GRANT_") && (
                  <a href={`https://tronscan.org/#/transaction/${it.txid}`} target="_blank" rel="noreferrer" className="text-black/40 hover:text-[#B8860B] flex-shrink-0" title="View on TronScan"><ExternalLink size={15} /></a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <h2 className="font-display font-extrabold text-2xl mb-4">Trades</h2>
      <div className="bg-white nb-border rounded-2xl nb-shadow overflow-hidden">
        {trades.length === 0 ? <div className="p-8 text-center text-black/40 font-semibold">No trades yet.</div> : (
          <div className="divide-y-2 divide-black/5">
            {trades.map((t) => (
              <div key={t.id} className="p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold truncate">{t.listing_title}</div>
                  <div className="text-xs text-black/50 font-medium">{t.from_user_name} ⇄ {t.to_user_name} · item value ${Number(t.agreed_value).toLocaleString()}</div>
                </div>
                <span className={`text-[10px] font-extrabold uppercase rounded-full px-2.5 py-1 border-2 border-black ${t.trade_status === "completed" ? "bg-green-200 text-green-800" : "bg-[#FBF7E9]"}`}>{t.trade_status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

